/**
 * LandXmlGrid.ts
 * -----------------------------------------------------------------------------
 * The survey graticule drawn under an imported LandXML map: minor and major grid
 * lines on the ground plane, with the site's own coordinates labelled along every
 * major line, on all four edges.
 *
 * WHY THE LABELS ARE THE FILE'S NUMBERS, NOT THE SCENE'S
 * ======================================================
 * The scene is recentred on the model and scaled to metres, because survey
 * coordinates run to seven or eight figures and float32 vertex buffers carry
 * seven digits total. Those scene coordinates are an implementation detail and
 * mean nothing to anyone. A grid is only useful if its numbers are the ones on
 * the surveyor's drawing, so every label is converted back through the frame to
 * the file's own easting/northing, in the file's own linear unit.
 *
 * The interval is likewise chosen in survey units — 500 ft, not 152.4 m — so the
 * labels come out round.
 */

/** How the viewer's metres relate to the file's survey coordinates. */
export interface SurveyFrame {
  /** Survey easting at viewer x = 0. */
  ox: number;
  /** Survey northing at viewer z = 0. */
  oy: number;
  /** Metres per survey linear unit. */
  k: number;
  /** The file's linear unit, as it declares it ("foot", "meter"). */
  unit: string;
}

export interface MapGridLabel {
  text: string;
  /** Viewer-space position on the ground plane. */
  x: number;
  z: number;
}

export interface MapGrid {
  /** Line-segment endpoint pairs in viewer space, y already applied. */
  minor: Float32Array;
  major: Float32Array;
  labels: MapGridLabel[];
  /** Text height in metres, sized to the grid so it reads at map zoom. */
  labelSize: number;
  /** Major interval, in survey units. */
  interval: number;
}

/** Roughly how many major divisions to put across the longer side. */
const TARGET_DIVISIONS = 8;

/** Minor lines per major cell. */
const MINOR_PER_MAJOR = 5;

/** Refuse to emit more than this many lines per axis — a guard against a file
 *  whose extent and unit combine into something absurd. */
const MAX_LINES_PER_AXIS = 400;

/** Label height as a fraction of a major cell. The grid is a reference, not the
 *  subject: at a twentieth of a cell a seven-figure coordinate is legible and
 *  still leaves the map itself the loudest thing on screen. */
const LABEL_HEIGHT_FRACTION = 1 / 20;

/** How far a major line runs past the grid border, in label heights. Minor lines
 *  stop at the border, so the overhang is what picks the majors out as the ruler's
 *  ticks and gives each label something to sit against. */
const TICK_OVERHANG = 1.0;

/** Clear space between the end of a tick and its label, in label heights. */
const LABEL_GAP = 0.8;

/** 1, 2 or 5 × 10ⁿ — the intervals a person reads without doing arithmetic. */
function niceStep(raw: number): number {
  if (!(raw > 0) || !Number.isFinite(raw)) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  for (const m of [1, 2, 5]) {
    if (raw <= m * pow) return m * pow;
  }
  return 10 * pow;
}

/**
 * Build the graticule for a model occupying `box` in viewer space, drawn at
 * height `y`. Returns null when the extent is degenerate.
 */
export function buildMapGrid(
  box: { minX: number; maxX: number; minZ: number; maxZ: number },
  y: number,
  frame: SurveyFrame,
): MapGrid | null {
  const k = frame.k || 1;
  if (!(box.maxX > box.minX) || !(box.maxZ > box.minZ)) return null;

  // Viewer → survey. Note z is negated (viewer z = −(northing − oy) · k), so the
  // viewer's MIN z is the survey's MAX northing.
  const eastAt = (x: number) => x / k + frame.ox;
  const northAt = (z: number) => -z / k + frame.oy;
  // …and back.
  const xOf = (east: number) => (east - frame.ox) * k;
  const zOf = (north: number) => -(north - frame.oy) * k;

  const e0 = eastAt(box.minX), e1 = eastAt(box.maxX);
  const n0 = northAt(box.maxZ), n1 = northAt(box.minZ);

  const major = niceStep(Math.max(e1 - e0, n1 - n0) / TARGET_DIVISIONS);
  const minor = major / MINOR_PER_MAJOR;

  // Snap the frame out to whole major cells so the border is itself a labelled line.
  const gE0 = Math.floor(e0 / major) * major;
  const gE1 = Math.ceil(e1 / major) * major;
  const gN0 = Math.floor(n0 / major) * major;
  const gN1 = Math.ceil(n1 / major) * major;

  if ((gE1 - gE0) / minor > MAX_LINES_PER_AXIS || (gN1 - gN0) / minor > MAX_LINES_PER_AXIS) return null;

  const xa = xOf(gE0), xb = xOf(gE1);
  const za = zOf(gN0), zb = zOf(gN1);

  // Text sized to the cell, not to the world, so it reads the same at any extent.
  const cell = Math.abs(xOf(gE0 + major) - xOf(gE0));
  const labelSize = cell * LABEL_HEIGHT_FRACTION;
  const tick = labelSize * TICK_OVERHANG;
  const gap = labelSize * LABEL_GAP;

  const zTop = Math.min(za, zb), zBot = Math.max(za, zb);
  const xLeft = Math.min(xa, xb), xRight = Math.max(xa, xb);

  // The border of the grid proper, and the same rectangle grown by one tick — a
  // major line spans the outer one, a minor line the inner.
  const zTickTop = zTop - tick, zTickBot = zBot + tick;
  const xTickLeft = xLeft - tick, xTickRight = xRight + tick;

  const minorPts: number[] = [];
  const majorPts: number[] = [];
  const labels: MapGridLabel[] = [];

  // Eastings — lines of constant E, running north/south.
  const steps = (a: number, b: number) => Math.round((b - a) / minor);
  for (let i = 0, n = steps(gE0, gE1); i <= n; i++) {
    const e = gE0 + i * minor;
    const x = xOf(e);
    if (i % MINOR_PER_MAJOR === 0) majorPts.push(x, y, zTickTop, x, y, zTickBot);
    else minorPts.push(x, y, zTop, x, y, zBot);
  }
  // Northings — lines of constant N, running east/west.
  for (let j = 0, n = steps(gN0, gN1); j <= n; j++) {
    const nn = gN0 + j * minor;
    const z = zOf(nn);
    if (j % MINOR_PER_MAJOR === 0) majorPts.push(xTickLeft, y, z, xTickRight, y, z);
    else minorPts.push(xLeft, y, z, xRight, y, z);
  }

  const decimals = Math.max(0, -Math.floor(Math.log10(major)));
  // No thousands separators: a seven-figure easting is already wide, and the
  // commas cost another two characters of the cell it has to fit in.
  const fmt = (v: number) => v.toFixed(decimals);

  // Labels sit clear of the ticks, not of the border…
  for (let i = 0, n = Math.round((gE1 - gE0) / major); i <= n; i++) {
    const e = gE0 + i * major;
    const x = xOf(e);
    const t = fmt(e);
    labels.push({ text: t, x, z: zTickTop - gap });
    labels.push({ text: t, x, z: zTickBot + gap });
  }
  // …and northings down the left and right. Horizontal like the rest: a plan is
  // read one way up, and turning half the numbers on their side to save a little
  // width makes them slower to read for no gain.
  for (let j = 0, n = Math.round((gN1 - gN0) / major); j <= n; j++) {
    const nn = gN0 + j * major;
    const z = zOf(nn);
    const t = fmt(nn);
    const half = (t.length * labelSize * 0.62) / 2;
    labels.push({ text: t, x: xTickLeft - gap - half, z });
    labels.push({ text: t, x: xTickRight + gap + half, z });
  }

  return {
    minor: new Float32Array(minorPts),
    major: new Float32Array(majorPts),
    labels,
    labelSize,
    interval: major,
  };
}
